import { sentenceCase } from "change-case"
import {
  Engagement,
  PaymentFrequencyEnum,
  VariableCompensation,
  VariableCompensationLabelEnum,
  VariableCompensationTypeEnum,
} from "@oysterhr/schema"
import {
  BenefitFrequency,
  OrderedBenefitFrequency,
  allowedAllowanceTypes,
  allowedBonusTypes,
} from "~/app/utils/constants"
import type { TFunction } from "react-i18next"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { useVariableCompensationTranslations } from "../hooks/variable-compensation/useVariableCompensationTranslations"

export const variableCompensationOptions = (
  t: TFunction,
  compensationType: VariableCompensationTypeEnum,
  afterFirstCutOff = false,
  isContractor = false,
) => {
  const allowanceOptions = allowedAllowanceTypes.map(allowanceType => ({
    label: t(`ALLOWANCE.labelOptions.${allowanceType}`, {
      ns: "variable_compensation/Common",
    }),
    value: allowanceType,
  }))
  const bonusOptions = allowedBonusTypes
    .filter(value => !afterFirstCutOff || value !== "SIGN_ON_BONUS")
    .filter(value => (isContractor && value !== "COMMISSION_PERCENTAGE") || !isContractor)
    .map(bonusType => ({
      label: t(`BONUS.labelOptions.${bonusType}`, {
        ns: "variable_compensation/Common",
      }),
      value: bonusType,
    }))

  if (compensationType === VariableCompensationTypeEnum.Allowance) {
    return allowanceOptions
  }
  return bonusOptions
}

export const afterFirstCutOffDate = (engagement: Engagement): boolean => {
  if (!engagement.startDate || !engagement.cutoffMonthDay) return false

  const startDate = DateTime.fromISO(engagement.startDate)
  const cutoffDate = DateTime.utc(startDate.year, startDate.month, engagement.cutoffMonthDay)
  const now = DateTime.utc().toLocal()

  // engagement starts in the same month, use next month as cutoff
  if (cutoffDate < startDate) {
    return now > cutoffDate.plus({ months: 1 })
  } else {
    return now > cutoffDate
  }
}

export const frequencyOptions = (t: TFunction, afterFirstCutOff = false) => {
  return OrderedBenefitFrequency.filter(
    value => !afterFirstCutOff || value !== BenefitFrequency.ONE_TIME,
  ).map(value => ({
    value,
    label: t(`GENERIC.frequencyOptions.${value}`, {
      ns: "variable_compensation/Common",
    }),
  }))
}

export const isOneTime = (paymentFrequency?: PaymentFrequencyEnum): boolean =>
  PaymentFrequencyEnum.OneTime === paymentFrequency

export const useGetVarCompLabelTranslation = (compensationType: VariableCompensationTypeEnum) => {
  const getTranslation = useVariableCompensationTranslations({
    compensationType,
    fileContext: "Common",
  })

  const getVarCompLabelTranslation = useCallback(
    (entry: VariableCompensation) => {
      const formattedType = getTranslation(`labelOptions.${entry.label}`, undefined, {
        defaultValue: sentenceCase(entry.label),
      })
      if (entry.label !== VariableCompensationLabelEnum.Other) return formattedType
      return `${formattedType} - ${entry.customLabel}`
    },
    [getTranslation],
  )

  return { getVarCompLabelTranslation }
}

export const supportsGuaranteedField = (compensationType: VariableCompensationTypeEnum) => {
  return compensationType === VariableCompensationTypeEnum.Bonus
}

export const fieldGuaranteedOptions = ({ hasNullOption = false }) => {
  return [
    {
      label: "Guaranteed",
      value: true,
    },
    {
      label: "Discretionary",
      value: false,
    },
    hasNullOption && {
      label: "Unknown",
      value: null,
    },
  ].filter(Boolean) as { label: string; value: boolean | null }[]
}

export const radioBinaryOptions = (t: TFunction) => {
  return [
    {
      label: t("label.yes"),
      value: true,
    },
    {
      label: t("label.no"),
      value: false,
    },
  ].filter(Boolean) as { label: string; value: boolean | null }[]
}

export const hasMonthsSelector = (paymentFrequency?: PaymentFrequencyEnum | "") =>
  paymentFrequency === BenefitFrequency.YEARLY
